import { Reference } from '@approvalmax/types';
import { DropdownEditor, Field } from '@approvalmax/ui';
import { domain } from 'modules/data';
import { PlainDataProvider } from 'modules/data-providers';
import { memo, useCallback } from 'react';

import { Header, Row, RowGroup } from '../../XeroCreateContactPopup.styles';
import { messages } from './PurchaseAndSalesSettingsSection.messages';
import { PurchaseAndSalesSettingsSectionProps } from './PurchaseAndSalesSettingsSection.types';

export const PurchaseAndSalesSettingsSection = memo<PurchaseAndSalesSettingsSectionProps>((props) => {
    const { contact, xeroContext, qa, onChangeReferenceField, onTrackingCategoryChange } = props;

    const renderTrackingCategory = useCallback(
        (
            categoryPropertyName: 'salesTrackingCategories' | 'purchasesTrackingCategories',
            index: number,
            qaStr: string
        ) => {
            const isSalesTrackingCategory = categoryPropertyName === 'salesTrackingCategories';
            const categoryName = xeroContext.trackingCategories[index].category.text;
            const value = isSalesTrackingCategory
                ? contact.salesTrackingCategories[index]
                : contact.purchasesTrackingCategories[index];
            const trackingCategoryItems = xeroContext.trackingCategories[index].options;

            return (
                <Row>
                    <Field title={<div>{categoryName}</div>}>
                        <PlainDataProvider items={trackingCategoryItems}>
                            <DropdownEditor
                                qa={qaStr}
                                placeholder={messages.dropdownPlaceholder}
                                value={value}
                                onChange={(newTracking: Reference) =>
                                    onTrackingCategoryChange(categoryPropertyName, index, newTracking, categoryName)
                                }
                            />
                        </PlainDataProvider>
                    </Field>
                </Row>
            );
        },
        [
            contact.salesTrackingCategories,
            contact.purchasesTrackingCategories,
            xeroContext.trackingCategories,
            onTrackingCategoryChange,
        ]
    );

    return (
        <RowGroup>
            <Row>
                <div>
                    <Header>{messages.salesSettingsHeader}</Header>

                    <Row>
                        <Field title={messages.salesAccountLabel}>
                            <PlainDataProvider items={xeroContext.accounts}>
                                <DropdownEditor
                                    qa={qa('sales-account-editor')}
                                    placeholder={messages.dropdownPlaceholder}
                                    value={contact.salesAccount || null}
                                    onChange={(newAccount: domain.XeroAccount) =>
                                        onChangeReferenceField('salesAccount', newAccount)
                                    }
                                />
                            </PlainDataProvider>
                        </Field>
                    </Row>

                    {xeroContext.trackingCategories[0] &&
                        renderTrackingCategory('salesTrackingCategories', 0, qa('sales-first-editor'))}

                    {xeroContext.trackingCategories[1] &&
                        renderTrackingCategory('salesTrackingCategories', 1, qa('sales-second-editor'))}
                </div>

                <div>
                    <Header>{messages.purchaseSettingsHeader}</Header>

                    <Row>
                        <Field title={messages.purchaseAccountLabel}>
                            <PlainDataProvider items={xeroContext.accounts}>
                                <DropdownEditor
                                    qa={qa('purchase-account-editor')}
                                    placeholder={messages.dropdownPlaceholder}
                                    value={contact.purchasesAccount || null}
                                    onChange={(newAccount: domain.XeroAccount) =>
                                        onChangeReferenceField('purchasesAccount', newAccount)
                                    }
                                />
                            </PlainDataProvider>
                        </Field>
                    </Row>

                    {xeroContext.trackingCategories[0] &&
                        renderTrackingCategory('purchasesTrackingCategories', 0, qa('purchases-first-editor'))}

                    {xeroContext.trackingCategories[1] &&
                        renderTrackingCategory('purchasesTrackingCategories', 1, qa('purchases-second-editor'))}
                </div>
            </Row>
        </RowGroup>
    );
});
